'use client';

import { useTranslations } from '@ifixit/i18n/hooks';
import { FaIcon } from '@ifixit/ui/components/icon';
import { Popover, PopoverContent, PopoverTrigger } from '@ifixit/ui/components/popover';
import { cn } from '@ifixit/ui/lib/utils';
import { useEffect } from 'react';
import { FeedbackCard } from './feedback-card';
import type { FeedbackTransport } from './types';
import { useFeedbackFlow } from './use-feedback-flow';

const THANKS_AUTO_CLOSE_MS = 2500;
const DEFAULT_MAX_TEXT_LENGTH = 2000;

export interface FeedbackPromptProps {
   surface: string;
   question: string;
   transport: FeedbackTransport;
   maxTextLength?: number;
   className?: string;
}

export function FeedbackPrompt({
   surface,
   question,
   transport,
   maxTextLength = DEFAULT_MAX_TEXT_LENGTH,
   className,
}: FeedbackPromptProps) {
   const t = useTranslations('FeedbackPrompt');
   const flow = useFeedbackFlow(surface, transport);

   useThanksAutoClose(flow.isOpen && flow.step === 'thanks', flow.setOpen);

   return (
      <Popover open={flow.isOpen} onOpenChange={flow.setOpen}>
         <PopoverTrigger asChild>
            <button
               type="button"
               className={cn(
                  'z-popover fixed top-1/2 right-0 flex -translate-y-1/2 cursor-pointer items-center gap-2 rounded-l-xl border border-r-0 border-gray-200 bg-white px-3 py-4 text-sm font-medium text-gray-900 shadow-md transition-colors [writing-mode:vertical-rl] hover:bg-gray-50 md:px-2.5 md:py-3 focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-brand-500/50',
                  className
               )}
            >
               <FaIcon
                  name="message-dots"
                  style="regular"
                  className="size-5 fill-current text-gray-700 md:size-4"
               />
               <span className="sr-only md:not-sr-only">{t('tab')}</span>
            </button>
         </PopoverTrigger>
         <PopoverContent
            sideOffset={8}
            collisionPadding={16}
            className="popover-left popover-align-center z-popover w-72 rounded-2xl p-4"
            aria-label={t('tab')}
         >
            <button
               type="button"
               className="button-ghost button-icon absolute top-2 right-2 size-8 text-gray-500"
               aria-label={t('close')}
               onClick={() => flow.setOpen(false)}
            >
               <FaIcon name="xmark" className="size-4 fill-current" />
            </button>
            <FeedbackCard
               question={question}
               step={flow.step}
               isSending={flow.isSending}
               hasError={flow.hasError}
               maxTextLength={maxTextLength}
               onVote={flow.vote}
               onSendText={flow.sendText}
            />
         </PopoverContent>
      </Popover>
   );
}

function useThanksAutoClose(isThanksVisible: boolean, setOpen: (isOpen: boolean) => void) {
   useEffect(() => {
      if (!isThanksVisible) {
         return;
      }

      const timer = window.setTimeout(() => setOpen(false), THANKS_AUTO_CLOSE_MS);
      return () => window.clearTimeout(timer);
   }, [isThanksVisible, setOpen]);
}
